/**
 * File: FeedbackButtons.tsx
 * Description: Main feedback buttons component - refactored and simplified
 * Author: System
 * Created: 2025-10-06
 * Last Modified: 2025-10-06
 */

import {
  HandThumbUpIcon,
  HandThumbDownIcon,
} from '@heroicons/react/24/outline';
import {
  HandThumbUpIcon as HandThumbUpSolidIcon,
  HandThumbDownIcon as HandThumbDownSolidIcon,
} from '@heroicons/react/24/solid';
import React, { useState, useRef, useCallback } from 'react';

import FeedbackService, {
  FeedbackCreateRequest,
} from '../../../services/feedbackService';
import { cn } from '../../../shared/utils';
import { logger } from '../../../utils/logger';

import { captureEnhancedContext } from './contextService';
import { FeedbackPopup } from './FeedbackPopup';
import type { FeedbackButtonsProps } from './types';

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  messageId,
  projectId = -1,
  feedbackContext,
  assistantRuntimeContext,
  onFeedback,
}) => {
  const [selectedFeedback, setSelectedFeedback] = useState<
    'up' | 'down' | null
  >(null);
  const [showPopup, setShowPopup] = useState(false);
  const [activeFeedback, setActiveFeedback] = useState<
    'up' | 'down' | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const upButtonRef = useRef<HTMLButtonElement>(null);
  const downButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleFeedbackClick = (type: 'up' | 'down') => {

    if (selectedFeedback === type) {
      // Deselect if clicking the same button
      setSelectedFeedback(null);
      setActiveFeedback(null);
      return;
    }

    setSelectedFeedback(type);
    setActiveFeedback(type);
    setShowPopup(true);
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  // Helper function to build context from semantic props
  const buildContextFromProps = () => {
    if (!feedbackContext) {
      return {
        section: 'chat',
        agent: 'UnknownAgent',
        context: 'No specific context provided',
      };
    }

    switch (feedbackContext.type) {
      case 'adr-suggestion':
        return {
          section: 'adr_suggestion',
          agent: 'ADRSuggestionAgent',
          context: JSON.stringify({
            suggestionId: feedbackContext.data.suggestionId,
            title: feedbackContext.data.title,
            description: feedbackContext.data.description,
            confidenceScore: feedbackContext.data.confidenceScore,
            contextSource: feedbackContext.data.contextSource,
            timestamp: new Date().toISOString(),
          }, null, 2),
        };
      
      case 'adr-generation':
        return {
          section: 'adr_generation',
          agent: 'ADRGenerationAgent',
          context: JSON.stringify({
            adrId: feedbackContext.data.adrId,
            title: feedbackContext.data.title,
            content: feedbackContext.data.content,
            status: feedbackContext.data.status,
            timestamp: new Date().toISOString(),
          }, null, 2),
        };
      
      case 'chat-message':
        return {
          section: 'chat',
          agent: 'ChatAgent',
          context: JSON.stringify({
            messageId: feedbackContext.data.messageId,
            messageContent: feedbackContext.data.messageContent,
            toolCalls: feedbackContext.data.toolCalls,
            timestamp: new Date().toISOString(),
          }, null, 2),
        };
      
      case 'custom':
        return {
          section: 'chat',
          agent: 'UnknownAgent',
          context: JSON.stringify(feedbackContext.data, null, 2),
        };
      
      case 'architecture_generation':
        return {
          section: 'architecture_generation',
          agent: 'ConceptualArchitectureAgent',
          context: JSON.stringify({
            sectionType: feedbackContext.data.sectionType,
            sectionId: feedbackContext.data.sectionId,
            title: feedbackContext.data.title,
            content: feedbackContext.data.content,
            status: feedbackContext.data.status,
            groundednessScore: feedbackContext.data.groundednessScore,
            timestamp: new Date().toISOString(),
          }, null, 2),
        };
      
      default:
        return {
          section: 'chat',
          agent: 'UnknownAgent',
          context: 'Unknown feedback context type',
        };
    }
  };

  const handlePopupSubmit = async (
    comment: string,
    includeContext: boolean,
    includeUserName: boolean,
  ) => {
    if (!selectedFeedback) {return;}

    const { section, agent, context } = buildContextFromProps();

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Call parent callback if provided
      if (onFeedback) {
        onFeedback(
          messageId,
          selectedFeedback,
          comment,
          includeContext,
          includeUserName,
        );
      }

      const enhancedContext = includeContext
        ? captureEnhancedContext(assistantRuntimeContext)
        : null;

      const feedbackData: FeedbackCreateRequest = {
        project_id: projectId,
        section,
        agent,
        feedback_data: {
          // Core feedback fields
          messageId: messageId || `${section}-${Date.now()}`,
          feedback_type: selectedFeedback === 'up' ? 'thumbs_up' : 'thumbs_down',
          comment,
          includeContext,
          includeUserName,
          timestamp: new Date().toISOString(),

          // Flexible agent output - store the semantic context
          agent_output: context,

          // Execution context
          execution_context: {
            enhanced_context: enhancedContext || undefined,
            feedback_context: feedbackContext,
            original_context: context,
          },
        },
      };

      const response = await FeedbackService.submitFeedback(feedbackData);

      setSubmitSuccess(true);

      // Auto-close popup after success
      setTimeout(() => {
        setShowPopup(false);
        setSubmitSuccess(false);
      }, 1500);
    } catch (error) {
      logger.error('FeedbackButtons - error submitting feedback:', error);
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to submit feedback',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-1">
        <button
          ref={upButtonRef}
          type="button"
          title="Good response"
          className={cn(
            'h-6 w-6 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground',
            activeFeedback === 'up' && 'text-success hover:text-success',
          )}
          onClick={() => handleFeedbackClick('up')}
        >
          {activeFeedback === 'up' ? (
            <HandThumbUpSolidIcon className="h-3 w-3" />
          ) : (
            <HandThumbUpIcon className="h-3 w-3" />
          )}
        </button>

        <button
          ref={downButtonRef}
          type="button"
          title="Poor response"
          className={cn(
            'h-6 w-6 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground',
            activeFeedback === 'down' && 'text-destructive hover:text-destructive',
          )}
          onClick={() => handleFeedbackClick('down')}
        >
          {activeFeedback === 'down' ? (
            <HandThumbDownSolidIcon className="h-3 w-3" />
          ) : (
            <HandThumbDownIcon className="h-3 w-3" />
          )}
        </button>
      </div>

      <FeedbackPopup
        isVisible={showPopup}
        onClose={handlePopupClose}
        onSubmit={handlePopupSubmit}
        feedbackType={selectedFeedback || 'up'}
        buttonRef={selectedFeedback === 'up' ? upButtonRef : downButtonRef}
        isSubmitting={isSubmitting}
        submitError={submitError || undefined}
        submitSuccess={submitSuccess}
      />
    </div>
  );
};